import { useMemo, useState } from "react";
import { courseMatchesAgeFilter } from "../utils/catalogAgeFilter.utils";
import { DEFAULT_CATALOG_SORT, sortCatalogCourses } from "../utils/catalogSort.utils";
import type { LandingCourse } from "../types/landing.types";

interface UseCatalogFiltersOptions {
  courses: LandingCourse[];
  pageSize: number;
  /** When false, list order comes from the API (sort is not re-applied client-side). */
  applyClientSort?: boolean;
  sortBy?: string;
  setSortBy?: (value: string) => void;
  search?: string;
  setSearch?: (value: string) => void;
  ageFilter?: string;
  setAgeFilter?: (value: string) => void;
  page?: number;
  setPage?: (value: number) => void;
}

export function useCatalogFilters({
  courses,
  pageSize,
  applyClientSort = true,
  sortBy: controlledSortBy,
  setSortBy: setControlledSortBy,
  search: controlledSearch,
  setSearch: setControlledSearch,
  ageFilter: controlledAgeFilter,
  setAgeFilter: setControlledAgeFilter,
  page: controlledPage,
  setPage: setControlledPage,
}: UseCatalogFiltersOptions) {
  const [search, setSearchState] = useState("");
  const [ageFilter, setAgeFilterState] = useState("");
  const [internalSortBy, setInternalSortBy] = useState(DEFAULT_CATALOG_SORT);
  const sortBy = controlledSortBy ?? internalSortBy;
  const setSortBy = setControlledSortBy ?? setInternalSortBy;
  const [page, setPageState] = useState(1);

  const activeSearch = controlledSearch ?? search;
  const activeAgeFilter = controlledAgeFilter ?? ageFilter;
  const activePage = controlledPage ?? page;

  const filtered = useMemo(() => {
    let result = [...courses];

    if (activeSearch.trim()) {
      const q = activeSearch.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.shortDescription.toLowerCase().includes(q)
      );
    }

    if (activeAgeFilter) {
      result = result.filter((c) => courseMatchesAgeFilter(c, activeAgeFilter));
    }

    if (applyClientSort) {
      result = sortCatalogCourses(result, sortBy);
    }

    return result;
  }, [courses, activeSearch, activeAgeFilter, sortBy, applyClientSort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(activePage, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const clearAgeFilter = () => {
    if (setControlledAgeFilter) {
      setControlledAgeFilter("");
      setControlledPage?.(1);
      return;
    }
    setAgeFilterState("");
    setPageState(1);
  };

  return {
    search: activeSearch,
    setSearch: (value: string) => {
      if (setControlledSearch) {
        setControlledSearch(value);
        return;
      }
      setSearchState(value);
      setPageState(1);
    },
    ageFilter: activeAgeFilter,
    setAgeFilter: (value: string) => {
      if (setControlledAgeFilter) {
        setControlledAgeFilter(value);
        return;
      }
      setAgeFilterState(value);
      setPageState(1);
    },
    sortBy,
    setSortBy,
    page: safePage,
    setPage: (value: number) => {
      if (setControlledPage) {
        setControlledPage(value);
        return;
      }
      setPageState(value);
    },
    totalPages,
    filteredCount: filtered.length,
    filtered,
    paginated,
    clearAgeFilter,
  };
}
